/* Flexi-Box build-your-own bundle.
 *
 * Two custom elements:
 * - <flexi-box-pdp>: PDP purchase block; adds the parent + selected items as
 *   nested cart lines (parent_id) and redirects to the cart.
 * - <flexi-box-modal-content>: contents modal body (pdp + cart modes). In cart
 *   mode it owns the customize state and saves diffs against the nested child
 *   lines (/cart/update.js by line key, /cart/add.js with parent_line_key).
 *
 * State is always read from the freshest [data-flexi-box-data] JSON blob —
 * the cart blob lives inside #main-cart-items .js-contents so AJAX re-renders
 * keep it current (snippets/wonderly-flexi-box-data.liquid).
 */

if (!customElements.get('flexi-box-modal-content')) {
  const getFlexiData = (mode) => {
    const el = document.querySelector(`[data-flexi-box-data="${mode}"]`);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  };

  const formatCents = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (error) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const clampQty = (value, max) => Math.min(Math.max(parseInt(value, 10) || 1, 1), max || 10);

  // Adds the whole box (parent + nested children) and redirects to the cart.
  const addBoxToCart = async (selections, data, onError) => {
    const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
    if (cart.items.some((item) => item.product_id === data.parentProductId)) {
      onError('already_in_cart');
      return;
    }

    const items = [
      { id: data.parentVariantId, quantity: 1 },
      ...selections.map((selection) => ({
        id: Number(selection.variantId),
        quantity: selection.quantity,
        parent_id: data.parentVariantId,
      })),
    ];

    const response = await fetch(routes.cart_add_url, {
      ...fetchConfig(),
      body: JSON.stringify({ items }),
    }).then((res) => res.json());

    if (response.status) {
      onError(response.description || response.message);
      return;
    }
    window.location = routes.cart_url;
  };

  class FlexiBoxModalContent extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.mode = this.getAttribute('mode') || 'pdp';
      this.busy = false;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      // With the description popup open, Escape closes just the popup —
      // stop it from bubbling to ModalDialog, which would close the modal.
      this.addEventListener('keyup', (event) => {
        const tooltip = this.querySelector('[data-flexi-tooltip]');
        if (event.code.toUpperCase() === 'ESCAPE' && tooltip && !tooltip.hidden) {
          event.stopPropagation();
          this.closeTooltip();
        }
      });

      this.modal = this.closest('modal-dialog');
      if (this.modal) {
        this.openObserver = new MutationObserver(() => {
          if (!this.modal.hasAttribute('open')) return;
          // ModalDialog.show() focuses the dialog, which scrolls the overlay
          // past its top padding — restore the breathing room above the panel.
          this.modal.scrollTop = 0;
          // Resync from the freshest cart data every time the modal opens.
          if (this.mode === 'cart') this.syncState();
        });
        this.openObserver.observe(this.modal, { attributes: true, attributeFilter: ['open'] });
      }

      if (this.mode === 'cart') {
        this.syncState();
      }
    }

    get data() {
      return getFlexiData(this.mode);
    }

    onClick(event) {
      // Quantity stepping itself is handled by the native <quantity-input>
      // element (global.js), which dispatches a change event we listen to.
      const info = event.target.closest('[data-flexi-info]');
      if (info) {
        this.openTooltip(info.closest('[data-flexi-card]'));
        return;
      }
      // Close the description popup on its X or a click on the dimmed scrim.
      if (
        event.target.closest('[data-flexi-tooltip-close]') ||
        event.target.matches('[data-flexi-tooltip]')
      ) {
        this.closeTooltip();
        return;
      }

      if (event.target.closest('[data-flexi-close]')) {
        if (this.modal) this.modal.hide();
        return;
      }
      if (event.target.closest('[data-flexi-add]')) {
        this.addBox();
        return;
      }
      if (event.target.closest('[data-flexi-save]')) {
        this.save();
      }
    }

    onChange(event) {
      if (event.target.matches('[data-flexi-qty]')) {
        event.target.value = clampQty(event.target.value, parseInt(event.target.max, 10));
      }
      if (event.target.matches('[data-flexi-check]')) {
        this.updateQtyDisabled(event.target.closest('[data-flexi-card]'));
      }
      if (event.target.matches('[data-flexi-qty], [data-flexi-check]')) {
        this.refreshSummary();
      }
    }

    // Unchecked items get their quantity controls disabled (visual fade is CSS).
    updateQtyDisabled(card) {
      if (!card) return;
      const check = card.querySelector('[data-flexi-check]');
      const enabled = Boolean(check && check.checked && !check.disabled);
      card.querySelectorAll('[data-flexi-qty], [data-flexi-qty-minus], [data-flexi-qty-plus]').forEach((el) => {
        el.disabled = !enabled;
      });
    }

    openTooltip(card) {
      if (!card) return;
      const tooltip = this.querySelector('[data-flexi-tooltip]');
      if (!tooltip) return;
      tooltip.querySelector('[data-flexi-tooltip-name]').textContent = card.dataset.title;
      tooltip.querySelector('[data-flexi-tooltip-teaser]').textContent = card.dataset.teaser;
      tooltip.hidden = false;
      const panel = tooltip.querySelector('.wonderly-flexi-box__tooltip-panel');
      if (panel) panel.focus();
    }

    closeTooltip() {
      const tooltip = this.querySelector('[data-flexi-tooltip]');
      if (tooltip) tooltip.hidden = true;
    }

    cards() {
      return Array.from(this.querySelectorAll('[data-flexi-card]'));
    }

    // Selected items: in pdp mode every available card; in cart mode the
    // checked ones (a disabled checkbox never counts).
    collectSelections() {
      return this.cards()
        .filter((card) => {
          const check = card.querySelector('[data-flexi-check]');
          if (check) return check.checked && !check.disabled;
          return card.dataset.available === 'true';
        })
        .map((card) => {
          const input = card.querySelector('[data-flexi-qty]');
          return {
            variantId: card.dataset.variantId,
            quantity: input ? clampQty(input.value, parseInt(input.max, 10)) : 1,
            priceCents: parseInt(card.dataset.price, 10) || 0,
            legacy: card.dataset.legacy === 'true',
          };
        });
    }

    // showMessage('') clears; showMessage() with no argument shows the
    // generic error fallback.
    showMessage(text) {
      const message = this.querySelector('[data-flexi-message]');
      if (!message) return;
      const content =
        text === ''
          ? ''
          : text || this.dataset.errorText || (window.cartStrings && window.cartStrings.error) || '';
      message.textContent = content;
      message.hidden = !content;
    }

    /* ---------- PDP mode ---------- */

    async addBox() {
      if (this.busy) return;
      const data = getFlexiData('pdp');
      if (!data || !data.parentVariantId) return;
      this.busy = true;
      this.showMessage('');
      const button = this.querySelector('[data-flexi-add]');
      if (button) button.disabled = true;
      try {
        await addBoxToCart(this.collectSelections(), data, (msg) => {
          this.showMessage(msg === 'already_in_cart' ? this.dataset.alreadyInCartText : msg);
        });
      } catch (error) {
        this.showMessage();
      } finally {
        this.busy = false;
        if (button) button.disabled = false;
      }
    }

    /* ---------- Cart mode ---------- */

    syncState() {
      const data = this.data;
      if (!data || !data.parentLineKey) return;
      const childByVariant = new Map(data.childLines.map((line) => [String(line.variantId), line]));
      // A parent with zero child lines can't come from this UI (the minimum
      // gate forbids emptying the box) — treat it as a fresh box with every
      // available item checked; saving re-adds them all as nested lines.
      const freshBox = data.childLines.length === 0;

      this.cards().forEach((card) => {
        const check = card.querySelector('[data-flexi-check]');
        const input = card.querySelector('[data-flexi-qty]');
        const child = childByVariant.get(card.dataset.variantId);
        if (check) {
          if (card.dataset.legacy === 'true' && !child) {
            // Removed on a previous save and no longer offered this week.
            check.checked = false;
            check.disabled = true;
            card.classList.add('wonderly-flexi-box__card--unavailable');
          } else if (!check.disabled) {
            check.checked = freshBox ? card.dataset.available === 'true' : Boolean(child);
          }
        }
        if (input) input.value = child ? child.quantity : 1;
        this.updateQtyDisabled(card);
      });
      this.showMessage('');
      this.refreshSummary();
    }

    refreshSummary() {
      if (this.mode !== 'cart') return;
      const data = this.data;
      if (!data || !data.parentVariantId) return;

      const total = this.collectSelections().reduce(
        (sum, selection) => sum + selection.priceCents * selection.quantity,
        0
      );
      const minimum = data.minimumCents || 0;

      const totalEl = this.querySelector('[data-flexi-total]');
      if (totalEl) totalEl.textContent = formatCents(total);

      const progress = this.querySelector('[data-flexi-progress]');
      if (progress) {
        progress.style.width = `${Math.min((total / minimum) * 100, 100)}%`;
        progress.classList.toggle('wonderly-flexi-box__progress-bar--met', total >= minimum);
      }

      const remaining = this.querySelector('[data-flexi-remaining]');
      if (remaining) {
        if (total < minimum) {
          remaining.textContent = (remaining.dataset.template || '')
            .replace('[amount]', formatCents(minimum - total))
            .replace('[minimum]', formatCents(minimum));
          remaining.hidden = false;
        } else {
          remaining.hidden = true;
        }
      }

      const save = this.querySelector('[data-flexi-save]');
      if (save) save.disabled = this.busy || total < minimum;
    }

    async save() {
      if (this.busy) return;
      const data = this.data;
      if (!data || !data.parentLineKey) return;

      const saveButton = this.querySelector('[data-flexi-save]');
      this.busy = true;
      if (saveButton) saveButton.disabled = true;
      this.showMessage('');

      const current = new Map(data.childLines.map((line) => [String(line.variantId), line]));
      const desired = new Map(
        this.collectSelections().map((selection) => [String(selection.variantId), selection.quantity])
      );

      const updates = {};
      current.forEach((line, variantId) => {
        if (!desired.has(variantId)) {
          updates[line.key] = 0;
        } else if (desired.get(variantId) !== line.quantity) {
          updates[line.key] = desired.get(variantId);
        }
      });
      const additions = Array.from(desired)
        .filter(([variantId]) => !current.has(variantId))
        .map(([variantId, quantity]) => ({
          id: Number(variantId),
          quantity,
          parent_line_key: data.parentLineKey,
        }));

      const cartItemsElement = document.querySelector('cart-items');
      const sections = cartItemsElement
        ? cartItemsElement.getSectionsToRender().map((section) => section.section)
        : [];

      try {
        let finalResponse = null;

        if (Object.keys(updates).length) {
          const body = { updates };
          if (!additions.length) {
            body.sections = sections;
            body.sections_url = window.location.pathname;
          }
          const response = await fetch(routes.cart_update_url, {
            ...fetchConfig(),
            body: JSON.stringify(body),
          }).then((res) => res.json());
          if (response.status && response.description) throw new Error(response.description);
          finalResponse = response;
        }

        if (additions.length) {
          const response = await fetch(routes.cart_add_url, {
            ...fetchConfig(),
            body: JSON.stringify({
              items: additions,
              sections,
              sections_url: window.location.pathname,
            }),
          }).then((res) => res.json());
          if (response.status && response.description) throw new Error(response.description);
          finalResponse = response;
        }

        if (finalResponse && finalResponse.sections && cartItemsElement) {
          // Mirror CartItems.updateQuantity's render path (assets/cart.js).
          cartItemsElement.getSectionsToRender().forEach((section) => {
            const sectionElement = document.getElementById(section.id);
            if (!sectionElement || !finalResponse.sections[section.section]) return;
            const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
            elementToReplace.innerHTML = cartItemsElement.getSectionInnerHTML(
              finalResponse.sections[section.section],
              section.selector
            );
          });
        }
        if (finalResponse) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'wonderly-flexi-box', cartData: finalResponse });
        }
        if (this.modal) this.modal.hide();
      } catch (error) {
        this.showMessage(error.message);
        await this.refreshAfterError();
      } finally {
        this.busy = false;
        this.refreshSummary();
      }
    }

    // A failed batch can leave earlier calls applied — pull the section fresh
    // so the data blob and the modal state reflect the real cart again.
    async refreshAfterError() {
      try {
        const html = await fetch(`${routes.cart_url}?section_id=main-cart-items`).then((res) => res.text());
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const fresh = parsed.querySelector('.js-contents');
        const target = document.querySelector('#main-cart-items .js-contents');
        if (fresh && target) target.innerHTML = fresh.innerHTML;
      } catch (error) {
        /* keep the modal usable with its current state */
      }
      this.syncState();
    }
  }

  class FlexiBoxPdp extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const button = this.querySelector('[data-flexi-add]');
      if (button) button.addEventListener('click', () => this.addBox(button));
    }

    // showMessage('') clears; showMessage() with no argument shows the
    // generic error fallback.
    showMessage(text) {
      const message = this.querySelector('[data-flexi-message]');
      if (!message) return;
      const content = text === '' ? '' : text || (window.cartStrings && window.cartStrings.error) || '';
      message.textContent = content;
      message.hidden = !content;
    }

    async addBox(button) {
      if (this.busy) return;
      const data = getFlexiData('pdp');
      if (!data || !data.parentVariantId) return;
      this.busy = true;
      this.showMessage('');
      button.disabled = true;

      const modalContent = document.querySelector('#FlexiBoxModal-pdp flexi-box-modal-content');
      const selections = modalContent
        ? modalContent.collectSelections()
        : data.items
            .filter((item) => item.available)
            .map((item) => ({ variantId: item.variantId, quantity: 1 }));

      try {
        await addBoxToCart(selections, data, (msg) => {
          const alreadyText =
            modalContent && msg === 'already_in_cart' ? modalContent.dataset.alreadyInCartText : msg;
          this.showMessage(alreadyText);
        });
      } catch (error) {
        this.showMessage();
      } finally {
        this.busy = false;
        button.disabled = false;
      }
    }
  }

  customElements.define('flexi-box-modal-content', FlexiBoxModalContent);
  customElements.define('flexi-box-pdp', FlexiBoxPdp);
}
